import { Component, Inject, Input } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Report } from '../../../models/report.model';
import { AuthService } from '../../../services/auth.service';
import { ReportService } from '../../../services/report.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ReportModalComponent } from '../../../components/report-modal/report-modal.component';
import { ReportCancelModalComponent } from '../../../components/report-cancel-modal/report-cancel-modal.component';
import { Table } from 'primeng/table';
import moment from 'moment';
import 'moment/locale/es';

/** Sufijo BEM del color semántico de un estado de reporte. */
export type ReportStatusVariant = 'Pending' | 'Progress' | 'Resolved' | 'Canceled';

@Component({
  selector: 'app-report-list',
  templateUrl: './report-list.component.html',
  styleUrl: './report-list.component.scss'
})
export class ReportListComponent {

  @Input() reports: Report[] = [];

  /** Texto de la bandeja vacía. Lo decide la pestaña, no la tabla. */
  @Input() emptyMessage: string = 'No hay reportes que mostrar.';

  /** Reportes cuya foto de amenidad falló al cargar (404, S3 caído). Vive aquí
      y no en el modelo: es estado de esta vista, no del dato. */
  private readonly brokenImages = new Set<string>();

  /** Columnas sobre las que busca el campo de texto. */
  public readonly searchFields: string[] = [
    'title',
    'description',
    'amenity.name',
    'streetBuilding.name',
    'houseFlat.number',
    'createdBy.names',
    'createdBy.paternalLastName'
  ];

  constructor(
    public authService: AuthService,
    public reportService: ReportService,
    private modalService: NgbModal,
    @Inject(DOCUMENT) private document: Document,
  ) {
    moment.locale('es');
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR cancel                               */
  /* -------------------------------------------------------------------------- */
  // region cancel
  /* La razón es obligatoria, así que la cancelación NO cabe en un confirm():
     va por modal con formulario. No hace falta refrescar nada al cerrar, el
     servicio vuelve a pedir las bandejas cuando la API responde. */
  cancel(
    report: Report
  ) {
    const modalRef = this.modalService.open(
      ReportCancelModalComponent
    );

    modalRef.componentInstance.report = report;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR delete                               */
  /* -------------------------------------------------------------------------- */
  // region delete
  /* Borrado FÍSICO. Solo aplica al reporte que levantó un admin y que nadie ha
     empezado a atender: quién puede llegar aquí lo decide report.canBeDeleted,
     no esta función. */
  async delete(
    report: Report
  ) {
    const flag = confirm(`¿Está seguro que desea borrar el reporte?`);

    if (flag) {
      await this.reportService.delete(
        report
      );
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR open                                */
  /* -------------------------------------------------------------------------- */
  // region open
  open(
    report: Report
  ) {
    const modalRef = this.modalService.open(
      ReportModalComponent
    );

    modalRef.componentInstance.report = report;
  }

  /**
   * Atajo de ratón: toda la fila abre el detalle. No se expone al teclado a
   * propósito — el botón del título ya es ese camino y duplicar el control
   * solo añade una parada de tabulación que dice lo mismo.
   *
   * Si el usuario estaba seleccionando texto dentro de la fila no se abre
   * nada: soltar el ratón tras arrastrar también dispara un click.
   */
  public openFromRow(
    report: Report
  ) {

    const selection = this.document.getSelection();

    if (selection && !selection.isCollapsed) {
      return;
    }

    this.open(report);

  }

  /**
   * Abre el detalle desde un control interno de la fila (hoy, el botón del
   * título) y corta la burbuja para que el handler de la fila no vuelva a
   * abrir el mismo modal. Sin esto el click se contaría dos veces.
   */
  public openFromControl(
    report: Report,
    event: Event
  ) {

    event.stopPropagation();

    this.open(report);

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR updateStatus                            */
  /* -------------------------------------------------------------------------- */
  // region updateStatus
  async updateStatus(
    report: Report
  ) {
    /* nextStatusString ya sabe a dónde va el reporte: el mensaje lo dice en
       vez de preguntar algo genérico, y la máquina de estados sigue viviendo
       solo en el modelo. */
    const flag = confirm(`¿Pasar el reporte "${report.title}" a ${report.nextStatusString.toLowerCase()}?`);

    if (flag) {
      await this.reportService.update(
        report
      );
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR search                               */
  /* -------------------------------------------------------------------------- */
  // region search
  /** Filtro global de la tabla. Vive aquí y no en la plantilla para no castear
      el EventTarget con $any dentro del HTML. */
  public search(
    table: Table,
    event: Event
  ) {

    const value = (event.target as HTMLInputElement).value;

    table.filterGlobal(
      value,
      'contains'
    );

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR resultsLabel                            */
  /* -------------------------------------------------------------------------- */
  // region resultsLabel
  /** "1 reporte" / "N reportes". Si hay filtro activo cuenta lo filtrado. */
  public resultsLabel(
    filteredCount?: number | null
  ): string {

    const total = filteredCount ?? this.reports.length;

    return total === 1
      ? '1 reporte'
      : `${total} reportes`;

  }

  /** "1 foto" / "N fotos" del reporte. */
  public attachmentLabel(
    report: Report
  ): string {

    const total = report.files.length;

    return total === 1
      ? '1 foto'
      : `${total} fotos`;

  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR locationLabel                            */
  /* -------------------------------------------------------------------------- */
  // region locationLabel
  /**
   * Dónde ocurre el reporte. La amenidad gana porque es el dato más concreto;
   * si no hay, se arma calle + casa; si tampoco, se dice que no hay ubicación.
   */
  public locationLabel(
    report: Report
  ): string {

    if (report.amenity) {
      return report.amenity.name;
    }

    const parts: string[] = [];

    if (report.streetBuilding) {
      parts.push(report.streetBuilding.name);
    }

    if (report.houseFlat) {
      parts.push(`Casa ${report.houseFlat.number}`);
    }

    return parts.length
      ? parts.join(' · ')
      : 'Sin ubicación';

  }

  /** Icono que acompaña a la ubicación, según de qué entidad venga. */
  public locationIcon(
    report: Report
  ): string {

    if (report.amenity) {
      return 'deck';
    }

    if (report.houseFlat || report.streetBuilding) {
      return 'home_work';
    }

    return 'not_listed_location';

  }

  /**
   * Foto de la amenidad para la celda de ubicación, o null si toca pintar el
   * icono: no hay amenidad, la amenidad no trae imagen, o la imagen falló.
   */
  public amenityImage(
    report: Report
  ): string | null {

    if (this.brokenImages.has(report.reportId)) {
      return null;
    }

    const url = report.amenity?.image?.urlFile?.trim();

    return url
      ? url
      : null;

  }

  /** La foto no cargó: se marca el reporte y la celda cae al icono. */
  public onAmenityImageError(
    report: Report
  ) {
    this.brokenImages.add(report.reportId);
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR reportedBy                              */
  /* -------------------------------------------------------------------------- */
  // region reportedBy
  /** Nombre de quien levantó el reporte, o un guion si la API no lo trae. */
  public reportedByLabel(
    report: Report
  ): string {

    const fullName = report.createdBy?.fullName?.trim();

    return fullName
      ? fullName
      : '—';

  }

  /**
   * Domicilio del residente que levantó el reporte, ya armado como
   * "Calle · Casa N". Cadena vacía cuando el reporte lo creó un admin o
   * cuando la API todavía no manda esos campos: la plantilla usa ese vacío
   * como condición y así nunca pinta una línea sin dato.
   */
  public reportedByAddressLabel(
    report: Report
  ): string {

    return report.isReportedByResident
      ? report.reportedByAddressLabel
      : '';

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR dates                                */
  /* -------------------------------------------------------------------------- */
  // region dates
  /** Fecha exacta, la que sirve para buscar el reporte en una bitácora. */
  public dateLabel(
    report: Report
  ): string {
    return moment(report.createdAt).format('DD MMM YYYY, HH:mm');
  }

  /** Antigüedad en lenguaje natural: "hace 3 días". */
  public relativeDateLabel(
    report: Report
  ): string {
    return moment(report.createdAt).fromNow();
  }

  /** Valor ISO para el atributo datetime de <time>. */
  public dateTime(
    report: Report
  ): string {
    return moment(report.createdAt).toISOString();
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR status                               */
  /* -------------------------------------------------------------------------- */
  // region status
  /** Sufijo BEM del color semántico del estado. */
  public statusVariant(
    report: Report
  ): ReportStatusVariant {

    /* La cancelación se pregunta antes del status: un cancelado conserva el
       status que traía y sin esto se pintaría con el color de ese estado. */
    if (report.isCanceled) {
      return 'Canceled';
    }

    switch (report.status) {
      case -1:
        return 'Pending';
      case 0:
        return 'Progress';
      default:
        return 'Resolved';
    }

  }

  /** Icono del estado. Acompaña al texto: el color nunca va solo. */
  public statusIcon(
    report: Report
  ): string {

    if (report.isCanceled) {
      return 'cancel';
    }

    switch (report.status) {
      case -1:
        return 'pending_actions';
      case 0:
        return 'autorenew';
      default:
        return 'task_alt';
    }

  }

  /**
   * Icono de la acción "Pasar a…". Habla del destino, no de la dirección: si
   * el reporte arranca trabajo se ve un play, si se cierra se ve el mismo
   * icono que lleva el chip de Completado. Se apoya en report.nextStatus para
   * no repetir aquí la máquina de estados.
   */
  public nextStatusIcon(
    report: Report
  ): string {

    return report.nextStatus === 0
      ? 'play_circle'
      : 'task_alt';

  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR cancelReason                             */
  /* -------------------------------------------------------------------------- */
  // region cancelReason
  /**
   * Motivo de la cancelación, o cadena vacía cuando no hay nada que mostrar
   * —el reporte sigue vivo, o la API todavía no manda el campo—. La plantilla
   * usa ese vacío como condición y así nunca pinta una línea sin dato.
   */
  public cancelReasonLabel(
    report: Report
  ): string {

    if (!report.isCanceled) {
      return '';
    }

    return report.cancelReason.trim();

  }

}
